import { ReactElement } from 'react';

import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { PARTICIPANT_ORDERS, ParticipantOrder } from '../../types/Event';
import { HANDICAP_LABELS } from '../../utils/handicapUtils';

interface EventPriorityEditorProps {
  /** Current priority value */
  value: ParticipantOrder | undefined;
  /** Called when the priority changes */
  onChange: (value: ParticipantOrder | undefined) => void;
  /** Whether to show the description text */
  showDescription?: boolean;
  /** Label to display before the toggle buttons */
  label?: string;
  /** Fallback value for display when value is undefined */
  fallbackValue?: ParticipantOrder;
  /** Whether clearing selection is allowed (setting to undefined) */
  allowClear?: boolean;
  /** Whether to show the Manager Choice option (team leagues only) */
  showMgrChoice?: boolean;
}

function getToggleValue(priority: ParticipantOrder | undefined): 'time' | 'index' | 'drawing' | 'mgr' | null {
  if (priority === PARTICIPANT_ORDERS.GHIN_INDEX) return 'index';
  if (priority === PARTICIPANT_ORDERS.DRAWING) return 'drawing';
  if (priority === PARTICIPANT_ORDERS.SIGNUP_TIME) return 'time';
  if (priority === PARTICIPANT_ORDERS.MGR_CHOICE) return 'mgr';
  return null;
}

function getParticipantOrder(toggleValue: 'time' | 'index' | 'drawing' | 'mgr'): ParticipantOrder {
  if (toggleValue === 'index') return PARTICIPANT_ORDERS.GHIN_INDEX;
  if (toggleValue === 'drawing') return PARTICIPANT_ORDERS.DRAWING;
  if (toggleValue === 'mgr') return PARTICIPANT_ORDERS.MGR_CHOICE;
  return PARTICIPANT_ORDERS.SIGNUP_TIME;
}

function getDescription(priority: ParticipantOrder | undefined): string {
  if (priority === PARTICIPANT_ORDERS.GHIN_INDEX) return `Event filled by ${HANDICAP_LABELS.INDEX}.`;
  if (priority === PARTICIPANT_ORDERS.DRAWING) return 'Event filled by drawing.';
  if (priority === PARTICIPANT_ORDERS.MGR_CHOICE) return 'Team manager selects who plays.';
  return 'Event filled by sign-up time.';
}

export default function EventPriorityEditor(props: EventPriorityEditorProps): ReactElement {
  const {
    value,
    onChange,
    showDescription = false,
    label = 'Entry priority:',
    fallbackValue,
    allowClear = false,
    showMgrChoice = false,
  } = props;

  const displayValue = getToggleValue(value) ?? getToggleValue(fallbackValue);
  const descriptionPriority = value ?? fallbackValue;

  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{
      alignItems: { xs: 'flex-start', md: 'center' }
    }}>
      <Stack direction="row" spacing={2} sx={{
        alignItems: "center"
      }}>
        <Typography variant="bodyMedium">{label}</Typography>
        <ToggleButtonGroup
          value={displayValue}
          exclusive
          onChange={(_, newValue) => {
            if (newValue === null) {
              if (allowClear) {
                onChange(undefined);
              }
            } else {
              onChange(getParticipantOrder(newValue));
            }
          }}
          size="small"
          aria-label="Entry priority"
        >
          <ToggleButton value="time">Time</ToggleButton>
          <ToggleButton value="index">Index</ToggleButton>
          <ToggleButton value="drawing">Drawing</ToggleButton>
          {showMgrChoice && <ToggleButton value="mgr">Manager</ToggleButton>}
        </ToggleButtonGroup>
      </Stack>
      {showDescription && (
        <Typography variant="body2" sx={{
          color: "text.secondary"
        }}>
          {getDescription(descriptionPriority)}
        </Typography>
      )}
    </Stack>
  );
}
